<template>
    <div class="bg-white shadow-sm sm:rounded-lg" v-if="(wallet.length || !isFreeUser) && !isLoadingWallet">
        <div class="px-4 py-5 sm:p-6">
            <div class="sm:flex sm:items-center sm:justify-between">
                <h3 class="text-base font-semibold text-gray-900">{{ $t('card_list.title') }}</h3>
                <button
                    v-if="!isFreeUser"
                    type="button"
                    @click.prevent="addCard"
                    class="mt-3 sm:mt-0 inline-flex items-center rounded-md bg-royal-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-xs cursor-pointer hover:bg-royal-blue-600"
                >{{ $t('card_list.add_button') }}</button>
            </div>

            <div class="rounded-md bg-green-50 p-4 mt-3" v-if="isCardAdded">
                <div class="flex">
                    <div class="shrink-0">
                        <CheckCircleIcon class="size-5 text-green-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3">
                        <p class="text-sm font-medium text-green-800">
                            {{ $t('card_list.added_success') }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="rounded-md bg-green-50 p-4 mt-3" v-if="isCardDeleted">
                <div class="flex">
                    <div class="shrink-0">
                        <CheckCircleIcon class="size-5 text-green-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3">
                        <p class="text-sm font-medium text-green-800">
                            {{ $t('card_list.deleted_success') }}
                        </p>
                    </div>
                    <div class="ml-auto pl-3">
                        <div class="-mx-1.5 -my-1.5">
                            <button type="button" @click.prevent="isCardDeleted = false" class="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50 focus:outline-hidden">
                                <span class="sr-only">{{ $t('card_list.dismiss') }}</span>
                                <XMarkIcon class="size-5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="rounded-md bg-blue-50 p-4 mt-3" v-if="wallet.length === 0 && !isLoadingWallet">
                <div class="flex">
                    <div class="shrink-0">
                        <InformationCircleIcon class="size-5 text-royal-blue-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                        <p class="text-sm text-royal-blue-500">
                            {{ $t('card_list.no_cards_info') }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="wallet.length && !isLoadingWallet">
                <div class="mt-3" v-for="(walletItem, index) in wallet" :key="`wallet-item-${index}-${walletItem.key}`">
                    <div class="rounded-md bg-gray-50 px-3 py-2.5 sm:flex sm:items-center sm:justify-between">
                        <h4 class="sr-only">Visa</h4>
                        <div class="sm:flex sm:items-center">
                            <svg v-if="walletItem.payment_system === 'visa'" class="h-8 w-auto sm:h-6 sm:shrink-0" viewBox="0 0 36 24" aria-hidden="true">
                                <rect width="36" height="24" fill="#224DBA" rx="4" />
                                <path fill="#fff" d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z" />
                            </svg>
                            <svg v-if="walletItem.payment_system === 'mc'" class="h-8 w-auto sm:h-6 sm:shrink-0" viewBox="0 0 39 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M38.6082 19.386V18.9218H38.4868L38.3465 19.2406L38.2068 18.9218H38.0847V19.386H38.171V19.0359L38.3022 19.338H38.3915L38.5227 19.0352V19.386H38.6082ZM37.8383 19.386V19.0009H37.9939V18.9225H37.5972V19.0009H37.7528V19.386H37.8383Z" fill="#F79410"/>
                                <path d="M24.8127 21.3828H14.3124V2.56079H24.8127V21.3828Z" fill="#FF5F00"/>
                                <path d="M14.9852 11.9703C14.9852 8.15215 16.7775 4.75106 19.5686 2.55927C17.5275 0.95652 14.9516 -0.000104904 12.1521 -0.000104904C5.52451 -0.000104904 0.1521 5.35913 0.1521 11.9703C0.1521 18.5814 5.52451 23.9407 12.1521 23.9407C14.9516 23.9407 17.5275 22.984 19.5686 21.3813C16.7775 19.1895 14.9852 15.7884 14.9852 11.9703Z" fill="#EB001B"/>
                                <path d="M38.9744 11.9703C38.9744 18.5814 33.602 23.9407 26.9744 23.9407C24.1749 23.9407 21.599 22.984 19.5571 21.3813C22.349 19.1895 24.1413 15.7884 24.1413 11.9703C24.1413 8.15216 22.349 4.75107 19.5571 2.55928C21.599 0.95653 24.1749 -9.53674e-05 26.9744 -9.53674e-05C33.602 -9.53674e-05 38.9744 5.35914 38.9744 11.9703Z" fill="#F79E1B"/>
                            </svg>
                            <div class="mt-3 sm:mt-0 sm:ml-4">
                                <div class="text-sm font-medium text-gray-900">{{ walletItem.pan }}</div>
    <!--                            <div class="mt-1 text-sm text-gray-600 sm:flex sm:items-center">-->
    <!--                                <div>Expires 12/20</div>-->
    <!--                                <span class="hidden sm:mx-2 sm:inline" aria-hidden="true">&middot;</span>-->
    <!--                                <div class="mt-1 sm:mt-0">Last updated on 22 Aug 2017</div>-->
    <!--                            </div>-->
                            </div>
                        </div>
                        <div class="mt-4 sm:mt-0 sm:ml-6 sm:shrink-0">
                            <button type="button" @click.prevent="removeWalletItem(walletItem)" class="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50">{{ $t('card_list.delete_button') }}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="rounded-md bg-blue-50 p-4 mt-3" v-if="wallet.length > 1">
                <div class="flex">
                    <div class="shrink-0">
                        <InformationCircleIcon class="size-5 text-royal-blue-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3 flex-1 md:flex md:justify-between">
                        <p class="text-sm text-royal-blue-500">
                            {{ $t('card_list.multiple_cards_info') }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-3" v-if="wallet.length === 1">
                <div class="flex">
                    <div class="shrink-0">
                        <ExclamationTriangleIcon class="size-5 text-yellow-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-yellow-700">
                            {{ $t('card_list.delete_last_warning') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";

import {validationMethods} from "@/_helpers/validation";

import {WalletItem} from "@/types/UserType";
import {addWalletCard, deleteWalletCard} from "@/data/requests/user";
import { ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/vue/20/solid'
// import CardsItemLoaderComponent from "@/components/Loaders/CardsItemLoaderComponent.vue";

export default defineComponent({
    name: "CardListComponent",

    components: {
        // CardsItemLoaderComponent,
        ExclamationTriangleIcon,
        CheckCircleIcon,
        XMarkIcon,
        InformationCircleIcon
    },

    data() {
        return {
            isLoadingWallet: false,
            isCardDeleted: false,

            errors: {} as { [key: string]: string[] },
        }
    },

    mounted() {
        this.loadWallet();

        if(this.isCardDeleted) {
            setInterval(() => {
                this.fetchWallet();
            }, 10000);
        }
    },

    computed: {
        ...mapGetters({
            user: "user/user",
            userToken: "user/token",

            wallet: "user/wallet",
        }),

        isCardAdded(): boolean {
            return location.hash === "#added";
        },

        isFreeUser(): boolean {
            return this.user?.subscription_type === 'free';
        }
    },

    methods: {
        ...mapActions({
            fetchWallet: "user/fetchWallet",

            errorAlert: "alerts/error",
        }),

        ...validationMethods(),

        loadWallet() {
            this.isLoadingWallet = true;

            this.fetchWallet()
                .then(() => {
                    this.isLoadingWallet = false;
                })
                .catch(() => {
                    this.errorAlert({
                        message: this.$t('card_list.load_error'),
                    });

                    this.isLoadingWallet = false;
                });
        },

        removeWalletItem(item: WalletItem) {
            deleteWalletCard(item.key, () => {
                this.loadWallet();

                this.isCardDeleted = true;
            }, () => {
                this.errorAlert({
                    message: this.$t('card_list.delete_error'),
                });
            })
        },

        addCard() {
            addWalletCard((data) => {
                if(data.url) {
                    location.href = data.url as string;
                } else {
                    this.errorAlert({
                        message: this.$t('card_list.add_error'),
                    });
                }
            }, (type, data) => {
                this.errorAlert({
                    message: data.message,
                });
            });
        }
    }
})
</script>